import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const { currentUser } = useAuth();

  // Load from local storage on initial render
  useEffect(() => {
    const storedCart = localStorage.getItem("guestCart");
    if (storedCart) {
      setCart(JSON.parse(storedCart));
    }
  }, []);

  // Save guest cart to localStorage on change
  useEffect(() => {
    if (!currentUser) {
      localStorage.setItem("guestCart", JSON.stringify(cart));
    }
  }, [cart, currentUser]);

  // 🔄 Fetch cart from Firestore on login
  useEffect(() => {
    const loadUserCart = async () => {
      if (currentUser) {
        const cartRef = doc(db, "carts", currentUser.uid);
        const docSnap = await getDoc(cartRef);
        if (docSnap.exists()) {
          const firestoreCart = docSnap.data().items || [];
          setCart(firestoreCart);
          localStorage.removeItem("guestCart");
        }
      }
    };
    loadUserCart();
  }, [currentUser]);

  // 🔄 Sync guest cart to Firestore after login
  useEffect(() => {
    const syncGuestCartToFirestore = async () => {
      if (currentUser) {
        const guestCart = localStorage.getItem("guestCart");
        if (guestCart) {
          const parsedCart = JSON.parse(guestCart);
          if (parsedCart.length > 0) {
            const cartRef = doc(db, "carts", currentUser.uid);
            await setDoc(cartRef, { items: parsedCart });
            setCart(parsedCart);
            localStorage.removeItem("guestCart");
          }
        }
      }
    };
    syncGuestCartToFirestore();
  }, [currentUser]);

  // 🔁 Save user cart to Firestore on change
  useEffect(() => {
    const updateFirestoreCart = async () => {
      if (currentUser && cart.length >= 0) {
        const cartRef = doc(db, "carts", currentUser.uid);
        await setDoc(cartRef, { items: cart });
      }
    };
    updateFirestoreCart();
  }, [cart, currentUser]);

  const addToCart = (product, quantity = 1) => {
    setCart((prevCart) => {
      const existing = prevCart.find((item) => item.id === product.id);
      if (existing) {
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevCart, { ...product, quantity }];
    });
  };

  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("guestCart");
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
